import type { ComponentType, ReactElement } from "react";

import { useSelector } from "react-redux";

import { useTranslate } from "@similarweb/i18n-provider";

import type { SelectionModalProps, TriggerProps } from "app/pages/sales-intelligence/common-components/column-picker";
import {
    ColumnsSelectionButtonContainer,
    ColumnsSelectionContainer,
} from "app/pages/sales-intelligence/common-components/column-picker";
import {
    type BaseActivatedPanelProps,
    ExcelExportButton,
    StyledInnerContainer,
    TableActionsPanel,
} from "app/pages/sales-intelligence/common-components/table-actions-panel";
import { selectAppPlatformFilterValue } from "app/pages/sales-intelligence/modules/leads-search-filters/store/selectors";
import ExportQuotaHoverContainer from "app/pages/sales-intelligence/modules/quota/containers/ExportQuotaContainer/ExportQuotaHoverContainer";
import { useExportQuota } from "app/pages/sales-intelligence/modules/quota/hooks/useExportQuota";
import multiSelectorTrackingService from "app/pages/sales-intelligence/services/tracking/createMultiSelectorPanelService";

import { useAppsColumnConfigQuery } from "../../query/queries/useAppsColumnConfigQuery";

type Props = {
    areTableActionsDisabled: boolean;
    renderExportPanel: ComponentType<BaseActivatedPanelProps>;
    renderSelectionModal: ComponentType<SelectionModalProps>;
};

const AppsTableActionsPanelContainer = ({
    areTableActionsDisabled,
    renderExportPanel,
    renderSelectionModal,
}: Props) => {
    const { translate } = useTranslate();

    const { isLoading, isQuotaReached, data: quotaData } = useExportQuota();

    const appPlatform = useSelector(selectAppPlatformFilterValue);
    const { isSuccess: isColumnConfigReady } = useAppsColumnConfigQuery(appPlatform);

    const renderSelectionTrigger = ({ onClick }: TriggerProps): ReactElement => (
        <ColumnsSelectionButtonContainer
            isDisabled={areTableActionsDisabled || !isColumnConfigReady}
            tooltipContent={translate("si.columns-selection.tooltip.change-columns")}
            onClick={onClick}
        />
    );

    const exportButton = (
        <ExcelExportButton
            isDisabled={areTableActionsDisabled || isLoading || isQuotaReached}
            onClick={() => {}}
        >
            {translate("si.common.button.export.excel")}
        </ExcelExportButton>
    );

    return (
        <TableActionsPanel>
            {({ activate }) => (
                <>
                    <StyledInnerContainer>
                        {isQuotaReached ? (
                            <ExportQuotaHoverContainer quota={quotaData}>{exportButton}</ExportQuotaHoverContainer>
                        ) : (
                            <ExcelExportButton
                                isDisabled={areTableActionsDisabled || isLoading || isQuotaReached}
                                onClick={() => {
                                    multiSelectorTrackingService.trackMultiSelectorMenuOpen(quotaData.remaining);
                                    activate(() => renderExportPanel);
                                }}
                            >
                                {translate("si.common.button.export.excel")}
                            </ExcelExportButton>
                        )}
                    </StyledInnerContainer>

                    <ColumnsSelectionContainer
                        renderSelectionTrigger={renderSelectionTrigger}
                        renderSelectionModal={renderSelectionModal}
                    />
                </>
            )}
        </TableActionsPanel>
    );
};

export default AppsTableActionsPanelContainer;
